<template>
    <Progressbar v-if="loading" />
    <NetworkError v-else-if="!clues.length && errors" @tryAgain="getData" />
    <div class="w-full h-full bg-gray-200 dark:bg-base-100 rounded-tr-3xl xl:px-9 pt-6 lg:pt-10 pb-14 " v-else>
        <div class="container">
            <div class="flex flex-col md:flex-row justify-between items-center md:items-center mb-6 lg:mb-8">
                <h3 class="flex items-center md:text-26 text-2xl dark:text-white text-biscay-700 font-semibold">
                    <i class="md:flex hidden w-2 h-2 dark:bg-white rounded-full bg-blue-700 ml-2"></i>
                    {{ __('Clues') }}
                </h3>
                <div class="flex mt-6 md:mt-0 flex-col lg:flex-row w-full md:w-auto justify-end">
                    <div class="flex mt-4 lg:mt-0 justify-end">
                        <input
                            class="input w-full h-10 xl:pl-36 placeholder-gray-320 md:w-64 xl:w-80 ml-2 bg-gray-350 bg-opacity-10 rounded-lg  transition duration-200 text-sm text-gray-350 px-3 font-medium dark:!bg-base-300 dark:!text-white  focus:bg-blue-700 focus:bg-opacity-10 border border-blue-700  focus:border-opacity-100"
                            type="text" :placeholder="__('Search...')" v-model="searchValue">
                        <button @click="search"
                            class=" bg-blue-600 disabled:opacity-70 disabled:!bg-blue-600 flex items-center h-10 md:px-5 px-3 rounded-md text-white font-semibold text-sm transition duration-200 hover:bg-blue-800 ">
                            {{ __('Search') }}
                        </button>
                    </div>
                    <div class="flex order-first justify-between lg:order-last">
                        <Dropdown customClass="inline-block z-1"
                            :labelClass="['mr-2 px-2', { 'btn-outline': !hasFilter }]">
                            <template #label>
                                <FunnelIcon class="w-5 h-5" />
                            </template>
                            <div class="mt-3 card card-compact dropdown-content w-72 bg-base-100 shadow">
                                <div class="card-body">
                                    <div class="form-control">
                                        <label class="label cursor-pointer">
                                            <span class="label-text">{{ __('Show conversions')
                                            }}</span>
                                            <input type="checkbox" class="toggle toggle-primary" name="converted"
                                                v-model="filters.converted" />
                                        </label>
                                    </div>
                                    <div class="flex flex-col" v-if="!filters.converted">
                                        <label class="text-dark-550 dark:text-gray-810 text-sm mb-1 font-medium" for="">
                                            {{ __('Status') }}
                                        </label>
                                        <select class="select w-full max-w-xs" v-model="filters.status">
                                            <option value="">{{ __('Select...') }}</option>
                                            <option v-for="item in statusTypes" :value="item">
                                                {{ __(item.replace('_', ' ').capitalize()) }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="text-dark-550 dark:text-gray-810 text-sm mb-1 font-medium">
                                            {{ __('Birthday filter') }}
                                        </label>
                                        <select class="select w-full max-w-xs" v-model="filters.birthday_filter">
                                            <option value="">{{ __('Select...') }}</option>
                                            <option v-for="item in birthdayFilterOptions" :key="item.value"
                                                :value="item.value">{{ __(item.label) }}</option>
                                        </select>
                                    </div>
                                    <div class="flex flex-col"
                                        v-if="['exact', 'month'].includes(filters.birthday_filter)">
                                        <label class="text-dark-550 dark:text-gray-810 text-sm mb-1 font-medium">
                                            {{ __('Birth month') }}
                                        </label>
                                        <select class="select w-full max-w-xs" v-model="filters.birth_month">
                                            <option value="">{{ __('Select...') }}</option>
                                            <option v-for="(month, index) in monthNames" :key="index" :value="index + 1">
                                                {{ __(month) }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="flex flex-col" v-if="filters.birthday_filter === 'exact'">
                                        <label class="text-dark-550 dark:text-gray-810 text-sm mb-1 font-medium">
                                            {{ __('Birth day') }}
                                        </label>
                                        <select class="select w-full max-w-xs" v-model="filters.birth_day">
                                            <option value="">{{ __('Select...') }}</option>
                                            <option v-for="day in birthDays" :key="day" :value="day">{{ day }}</option>
                                        </select>
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="text-dark-550 dark:text-gray-810 text-sm mb-1 font-medium" for="">
                                            {{ __('Referred user') }}
                                        </label>
                                        <v-select dir="rtl" :options="meta.user"
                                            :reduce="(option) => option.id.toString()" id="report_to_id"
                                            v-model="filters.report_to_id" :placeholder="__('Select...')" class="input"
                                            :filterable="false" :clearable="true"
                                            @search="fetchOptions({ search: $event }, 'user')" :loading="loadingBtn"
                                            :getOptionLabel="(option) => option.name ?? __('Select...')">
                                            <template #no-options="{ search, searching }">
                                                <template v-if="searching">
                                                    <div class="opacity-60">{{ __('Nothing found to display') }}.</div>
                                                </template>
                                                <div v-else class="opacity-60">{{ __('Start typing to search')
                                                }}.</div>
                                            </template>
                                        </v-select>
                                    </div>
                                    <div class="card-actions">
                                        <button class="btn btn-primary btn-block" :disabled="birthdayFilterIncomplete"
                                            @click="setFilter">
                                            {{ __('Apply Filter') }}</button>
                                    </div>
                                </div>
                            </div>
                        </Dropdown>
                        <Dropdown labelClass="btn-outline" customClass="mr-2">
                            <ul
                                class="menu dropdown-content bg-base-100 rounded-box z-[2] w-52 p-2 shadow lg:!end-auto">
                                <li v-if="can('clue.create')">
                                    <label for="excel-modal">
                                        {{ __('Import leads') }}
                                    </label>
                                </li>
                                <li v-if="can('clue.edit.all')">
                                    <label for="birthdate-excel-modal">
                                        {{ __('Import lead birthdates') }}
                                    </label>
                                </li>
                                <li> <label for="message-modal">
                                        {{ __('Send SMS') }}
                                    </label></li>
                            </ul>
                        </Dropdown>

                        <div class="flex justify-start order-first lg:order-last lg:mr-2 flex-1"
                            v-if="can('clue.create')">
                            <router-link :to="{ name: 'user.clue.create' }" class="btn btn-outline btn-info btn-base">
                                <span class="mt-0.5">{{ __('Generate leads') }}</span>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
            <div class="rounded-2xl dark:bg-base-300 bg-white">
                <div class=" overflow-auto">
                    <table class="table table-xs md:table-sm xl:table-md" v-if="clues.length">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th> {{ __('Full Name') }} </th>
                                <th> {{ __('Phone number') }} </th>
                                <th> {{ __('Date of birth') }} </th>
                                <th> {{ __('Source') }} </th>
                                <th> {{ __('Status') }} </th>
                                <th> {{ __('Referred user') }} </th>
                                <th> {{ __('Created At') }} </th>
                                <th> {{ __('Update date') }} </th>
                                <th> {{ __('Action') }} </th>
                            </tr>

                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in clues">
                                <td>{{ (meta.per_page * (meta.current_page - 1)) + index + 1 }}</td>
                                <td> {{ item.name }} </td>
                                <td> {{ item.phone || '---' }} </td>
                                <td dir="ltr"> {{ item.birthdate || '---' }} </td>
                                <td> {{ item.lead_source || '---' }} </td>
                                <td> {{ __(item.status?.replace('_', ' ')?.capitalize()) || '---' }} </td>
                                <td> {{ item.report_to }} </td>
                                <td> {{ item.created_at }}</td>
                                <td> {{ item.updated_at }}</td>
                                <td>
                                    <router-link :to="{ name: 'user.clue.show', params: { id: item.id } }"
                                        :class="{ 'btn-disabled': !can('clue.view.all') && !can('clue.view.self', item.self) }"
                                        class="btn btn-outline btn-primary btn-sm ml-2">
                                        {{ __('Show') }}
                                    </router-link>
                                    <router-link :to="{ name: 'user.clue.edit', params: { id: item.id } }"
                                        :class="{ 'btn-disabled': !can('clue.edit.all') && !can('clue.edit.self', item.self && item.status != 'converted')}"
                                        class="btn btn-outline btn-info btn-sm ml-2">
                                        {{ __('Edit') }}
                                    </router-link>
                                    <label @click="setItem(item)" for="delete-modal"
                                        :class="{ 'btn-disabled': !can('clue.delete.all') && !can('clue.delete.self', item.self && item.status != 'converted') }"
                                        class="btn btn-outline btn-error btn-sm">
                                        {{ __('Delete') }}
                                    </label>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="col-span-12 flex flex-col items-center mt-9" v-else>
                        <NotFound />
                    </div>
                </div>
            </div>
            <Pagination :meta="meta" @setPage="setPage" class="!mt-6" />
        </div>
    </div>
    <Teleport to="#app">
        <input type="checkbox" id="delete-modal" class="modal-toggle" />
        <label class="modal" for="delete-modal">
            <label class="modal-box" for="">
                <h3 class="font-bold text-lg">{{ __('Delete clue') }}</h3>
                <p class="py-4">{{ __('Are you sure to delete this clue?') }}</p>
                <div class="modal-action">
                    <label class="btn btn-sm" for="delete-modal"
                        :class="{ 'pointer-events-none opacity-60': loadingBtn }">{{
                            __('Cancel') }}</label>
                    <button class="btn btn-error btn-sm text-white" @click="removeClue"
                        :class="{ 'loading': loadingBtn }">
                        {{ __('Delete') }}
                    </button>
                </div>
            </label>
        </label>
    </Teleport>
    <Teleport to="#app">
        <input type="checkbox" id="birthdate-excel-modal" class="modal-toggle" />
        <div class="modal">
            <Form @submit="importClueBirthdates" class="modal-box max-w-2xl" v-slot="{ errors }">
                <h3 class="font-bold text-lg">{{ __('Import lead birthdates') }}</h3>
                <ul class="list-group mt-3">
                    <li class="list-group-item">✔ {{ __('The first column must contain the mobile number') }}</li>
                    <li class="list-group-item">✔ {{ __('The second column must contain the Jalali birthdate') }}</li>
                    <li class="list-group-item">✔ {{ __('Existing leads are updated and no new lead is created') }}</li>
                    <li class="list-group-item">✔ {{ __('Example birthdate format') }}: <b dir="ltr">1370/04/10</b></li>
                </ul>
                <div v-if="birthdateImportSummary" class="mt-4 rounded-lg bg-base-200 p-3">
                    <div class="flex flex-wrap gap-4 text-sm font-semibold">
                        <span>{{ __('Processed rows') }}: {{ birthdateImportSummary.processed }}</span>
                        <span class="text-success">{{ __('Updated records') }}: {{ birthdateImportSummary.updated }}</span>
                        <span class="text-error">{{ __('Failed rows') }}:
                            {{ birthdateImportSummary.processed - birthdateImportSummary.updated }}</span>
                    </div>
                    <div class="max-h-48 overflow-auto mt-3" v-if="birthdateImportSummary.failures.length">
                        <table class="table table-xs">
                            <thead>
                                <tr>
                                    <th>{{ __('Row') }}</th>
                                    <th>{{ __('Reason') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="failure in birthdateImportSummary.failures" :key="`${failure.row}-${failure.reason}`">
                                    <td>{{ failure.row }}</td>
                                    <td>{{ __(failure.reason) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p class="text-xs text-warning mt-2" v-if="birthdateImportSummary.failures_truncated">
                        {{ __('Only the first 100 failures are displayed') }}
                    </p>
                </div>
                <div class="flex items-center mt-5 w-full">
                    <Field type="file" class="file-input file-input-bordered file-input-primary pr-0 w-full"
                        accept=".csv,.xlsx,.xls" :class="{ 'file-input-error': errors.excel }" name="excel" />
                </div>
                <div class="modal-action">
                    <button class="btn btn-sm btn-success" type="submit" :disabled="loadingBtn">
                        <span class="loading loading-spinner" v-if="loadingBtn"></span>
                        {{ __('Import') }}
                    </button>
                    <label class="btn btn-sm" for="birthdate-excel-modal">{{ __('Cancel') }}</label>
                </div>
            </Form>
        </div>
    </Teleport>
    <Teleport to="#app">
        <input type="checkbox" id="excel-modal" class="modal-toggle" />
        <div class="modal">
            <Form @submit="importClue" class="modal-box" v-slot="{ errors }">
                <h3 class="font-bold text-lg">{{ __('Upload Excel file') }}</h3>
                <ul class="list-group mt-3">
                    <li class="list-group-item"> ✔ ستون اول حتما شامل شماره تماس باشد.</li>
                    <li class="list-group-item"> ✔ در هر ردیف یک شماره وارد شود.</li>
                    <li class="list-group-item"> ✔ شماره بصورت کامل با فرمت<b> 09xxxxxxxxx </b> وارد نمایید.</li>
                    <li class="list-group-item"> ✔ ستون دوم باید شامل نام باشد.</li>
                    <li class="list-group-item"> ✔ ستون سوم باید شامل نام خانوادگی باشد.</li>
                    <li class="list-group-item"> ✔ ستون چهارم می تواند شامل شناسه ارجاع دهنده باشد و در صورت خالی گذاشتن
                        آن
                        به خودتان ارجاع داده می شود.</li>
                    <li class="list-group-item"> ✔ ستون پنجم می تواند شامل منبع سرنخ باشد، منبع وارد شده باید حداقل با
                        یکی
                        از منابع موجود همخوانی داشته باشد.</li>
                    <li class="list-group-item text-blue-500"><a :href="excelFile">دانلود فایل نمونه</a>
                    </li>
                </ul>
                <div class="flex items-center mt-5 w-full">
                    <Field type="file" class="file-input file-input-bordered file-input-primary pr-0 w-full"
                        accept=".csv,.xlsx,.xls" :class="{ 'file-input-error': errors.excel }" name="excel" />
                </div>
                <div class="modal-action">
                    <button class="btn btn-sm btn-success" type="submit" :disabled="loadingBtn">
                        <span class="loading loading-spinner" v-if="loadingBtn"></span>
                        {{ __('Import') }}
                    </button>
                    <label class="btn btn-sm" for="excel-modal">
                        {{ __('Cancel') }}
                    </label>
                </div>
            </Form>
        </div>
    </Teleport>
    <Teleport to="#app">
        <input type="checkbox" id="message-modal" class="modal-toggle" />
        <div class="modal">
            <Form class="modal-box overflow-visible" v-slot="{ errors }" @submit="sendSms">
                <h3 class="font-bold text-lg">{{ __('Send SMS') }}</h3>
                <div class="flex flex-col mt-4">
                    <label class="text-dark-550 dark:text-gray-810 text-sm mb-1 font-medium" for="">
                        {{ __('Clue') }}
                    </label>
                    <Field name="clue_id" v-slot="{ field }" v-model="clue.clue_id" rules="required">
                        <v-select dir="rtl" :options="meta.clue" :reduce="(option) => option.id"
                            :class="{ 'input-error': errors.clue_id }" @update:modelValue="val => clue.clue_id = val"
                            :modelValue="field.value ? parseInt(field.value) : null" :placeholder="__('Select...')" v-bind="field" class="input"
                            :filterable="false" :clearable="false" @search="fetchOptions({ search: $event }, 'clue')"
                            :loading="meta.type == 'clue' && loadingBtn"
                            :getOptionLabel="(option) => option.name ?? __('Select...')">
                            <template #no-options="{ search, searching }">
                                <template v-if="searching">
                                    <div class="opacity-60">{{ __('Nothing found to display') }}.</div>
                                </template>
                                <div v-else class="opacity-60">{{ __('Start typing to search')
                                    }}.</div>
                            </template>
                        </v-select>
                    </Field>
                </div>
                <div class="flex flex-col mt-4">
                    <label class="text-dark-550 dark:text-gray-810 text-sm mb-1 font-medium" for="">
                        {{ __('Ready message') }}
                    </label>
                    <Field name="message_id" v-slot="{ field }" v-model="clue.message_id" rules="required">
                        <v-select dir="rtl" :options="meta.save_message" :reduce="(option) => option.id"
                            :class="{ 'input-error': errors.message_id }"
                            @update:modelValue="val => clue.message_id = val" :modelValue="field.value ? parseInt(field.value) : null"
                            :placeholder="__('Select...')" v-bind="field" class="input" :filterable="false"
                            :clearable="false" @search="fetchOptions({ search: $event }, 'save_message')"
                            :loading="meta.type == 'save_message' && loadingBtn"
                            :getOptionLabel="(option) => option.name ?? __('Select...')">
                            <template #no-options="{ search, searching }">
                                <template v-if="searching">
                                    <div class="opacity-60">{{ __('Nothing found to display') }}.</div>
                                </template>
                                <div v-else class="opacity-60">{{ __('Start typing to search')
                                    }}.</div>
                            </template>
                        </v-select>
                    </Field>
                </div>
                <div class="modal-action">
                    <button class="btn btn-sm btn-primary" type="submit" :disabled="loadingBtn">
                        <span class="loading loading-spinner" v-if="loadingBtn"></span>
                        {{ __('Send') }}
                    </button>
                    <label class="btn btn-sm" for="message-modal">
                        {{ __('Cancel') }}
                    </label>
                </div>
            </Form>
        </div>
    </Teleport>
</template>
<script setup>
import { ref, onMounted, computed, onUpdated, reactive } from 'vue';
import { FunnelIcon } from '@heroicons/vue/24/outline';
import { Field, Form } from "vee-validate";
import { birthdayDays, birthdayFilterIsIncomplete, birthdayFilterOptions, jalaliMonthNames, normalizeBirthdayFilters } from '../../composables/birthdayFilters';

const { getClues, loading, errors, meta, router, remove, route, loadingBtn, clue, clues, birthdateImportSummary, sendSms, fetchOptions, importClue, importClueBirthdates, removeClue } = useDashboard();
const searchValue = ref(route.query.search || '');
const statusTypes = ['new', 'assigned', 'in_process', 'recycled', 'dead'];
const monthNames = jalaliMonthNames;
const { status, report_to_id, converted, birthday_filter, birth_month, birth_day } = route.query;
const auth = useAuthStore();
const filters = reactive({
    status: status || '',
    report_to_id: report_to_id || '',
    converted: converted === 'true' || converted === true,
    birthday_filter: birthday_filter || '',
    birth_month: birth_month ? Number(birth_month) : '',
    birth_day: birth_day ? Number(birth_day) : '',
});
const birthDays = computed(() => birthdayDays(filters.birth_month));
const birthdayFilterIncomplete = computed(() => birthdayFilterIsIncomplete(filters));
if (filters.report_to_id) fetchOptions({ search: filters.report_to_id }, 'user');
const hasFiltered = () => Object.values(filters).filter(value => value).length;
const hasFilter = ref(hasFiltered());
const setQuery = (key, value) => {
    getClues(window.setQuery(router, route, key, value));
}
const setFilter = () => {
    if (filters.converted == true) filters.status = '';
    normalizeBirthdayFilters(filters);
    hasFilter.value = hasFiltered();
    getClues(window.setQueries(router, { ...route.query, ...filters, page: undefined }));
}
const controleState = (e, filter) => {
    state.value = e.target.textContent.trim()
    setQuery('status', filter);
}
const setPage = (p) => {
    setQuery('page', p.toString());
};
const setItem = (item) => Object.assign(remove, item);
const search = () => {
    setQuery('search', searchValue.value)
}
const getData = () => getClues(route.query)
onMounted(() => {
    getData();
});
const excelFile = computed(() => window.APP_URL + '/assets/uploads/basic/clue-example.xlsx');
// onUpdated(() => state.value = document.querySelector(`[data-filter-id=${route.query.status || 'all'}]`)?.textContent?.trim())
</script>
